<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import numberMixin from '../../../../mixins/numberMixin';
import ConfirmDialog from '../../../../components/TwConfirmDialog';
import salesTypeEnums from '../../../../variables/ski/school/sales-type-enums';
import statusEnums from '../../../../variables/ski/school/status-type-enums';

export default {
  name: 'SkiSchoolAvailableProducts',
  data() {
    return {
      selectedArea: null,
    };
  },
  components: {
    ConfirmDialog,
  },
  mixins: [numberMixin],
  computed: {
    ...mapGetters({
      participants: 'booking/getParticipantsWithFullName',
      availableDates: 'booking/getAvailableDates',
      mappedAvailableSchoolProducts: 'skischool/getAvailableSkiSchoolProducts',
      availableSchoolProductsPersonCategoryHeaders: 'skischool/getAvailableSkiSchoolProductsPersonCategoryHeaders',
      defaultAreaId: 'booking/getDefaultAreaId',
      availableNumberOfDays: 'skischool/getAvailableNumberOfDays',
      availableWeekDays: 'skischool/getAvailableWeekDays',
      availableLessonDurations: 'skischool/getAvailableLessonDurations',
      availableNumberOfPersons: 'skischool/getAvailableNumberOfPersons',
      availableSkiTypes: 'skischool/getAvailableSkiTypes',
      availableLessonTypes: 'skischool/getAvailableLessonTypes',
    }),
    ...mapState({
      participantsLoading: state => state.booking.participants.itemsLoading,
      participantsError: state => state.booking.participants.itemsError,
      selectedBookingParticipantId: state => state.skischool.search.selectedBookingParticipantId,
      selectedNumberOfDays: state => state.skischool.search.selectedNumberOfDays,
      selectedLessonType: state => state.skischool.search.selectedLessonTypeId,
      selectedMultipleSkiTypes: state => state.skischool.search.selectedSkiTypes,
      selectedWeekDayNumber: state => state.skischool.search.selectedWeekDayNumber,
      selectedLessonDuration: state => state.skischool.search.selectedLessonDuration,
      selectedNumberOfPersons: state => state.skischool.search.selectedNumberOfPersons,
      meetingPoints: state => state.skischool.meetingPoints.items,
      bookingParticipants: state => state.skischool.bookingParticipants.items,
      skischools: state => state.skischool.schools.items,
      availableSchoolProducts: state => state.skischool.availableSchoolProducts,
      booking: state => state.booking.booking,
      user: state => state.app.auth.user,
      areas: state => state.booking.areas.items,
    }),
    canSearch() {
      return this.booking.id
        && this.booking.stays
        && this.booking.stays.length
        && this.participants
        && this.participants.length
        && this.selectedBookingParticipantId
        && (this.selectedArea || this.defaultAreaId);
    },
    loading() {
      return this.participantsLoading
        || this.skischools.itemsLoading
        || this.availableSchoolProducts.itemsLoading
        || this.availableSchoolProducts.saveLoading;
    },
    errorOccurred() {
      return this.skischools.itemsError
        || this.availableSchoolProducts.itemsError
        || this.participantsError
        || this.availableSchoolProducts.saveError;
    },
    areaParticipants() {
      return this.participants.filter(participant => participant.areaId === this.getSelectedAreaId());
    },
  },
  methods: {
    ...mapActions({
      loadAvailableSchoolPrices: 'skischool/availableSchoolPricesLoad',
      loadSchoolsByBookingId: 'skischool/schoolsByBookingIdLoad',
      createSchool: 'skischool/schoolCreate',
      deleteSchool: 'skischool/schoolDelete',
      setBookingParticipant: 'skischool/setBookingParticipant',
      setNumberOfDays: 'skischool/setNumberOfDays',
      setLessonType: 'skischool/setLessonType',
      setWeekDayNumber: 'skischool/setWeekDayNumber',
      setLessonDuration: 'skischool/setLessonDuration',
      setNumberOfPersons: 'skischool/setNumberOfPersons',
      setSkiTypes: 'skischool/setSkiTypes',
      setClearFilters: 'skischool/setClearFilters',
    }),
    createSkiSchoolProduct(params) {
      const priceGross = params.price.priceGross;
      const priceNet = params.price.priceNet;
      const personCategoryAges = `(${params.price.personCategory.ageFrom}-${params.price.personCategory.ageTo} years)`;
      let startDateTime = null;
      const targetWeekDay = params.product.weekDay;
      const areaId = this.getSelectedAreaId();
      const bookingStay = this.getBookingStayByAreaId(areaId);

      const arrivalDate = this.$moment(bookingStay.arrivalDate);
      const departureDate = this.$moment(bookingStay.departureDate);
      const daysDifference = departureDate.diff(arrivalDate, 'days');

      for (let i = 0; i <= daysDifference; i++) {
        const currentDate = this.$moment(arrivalDate).add(i, 'days');
        if (targetWeekDay === currentDate.day()) {
          startDateTime = `${currentDate.format('YYYY-MM-DD')} 00:00:00`;
          break;
        }
      }

      const skiSchool = {
        bookingParticipantId: this.selectedBookingParticipantId,
        productsSkiSchoolSeasonId: params.product.season.id,
        productsSkiSchoolSeasonPersonCategoryId: params.price.personCategory.id,
        productsSkiSchoolSeasonLevelId: params.level.id,
        productsSkiSchoolSkiTypeId: params.product.skiType.id,
        productsSkiSchoolLessonTypeId: params.product.lessonType.id,
        productsSkiSchoolMeetingPointId: null,
        status: statusEnums.INITIATED,
        salesType: salesTypeEnums.BOOKING,
        supplierRef: null,
        supplierComment: null,
        customerComment: null,
        weekDay: targetWeekDay,
        startDateTime,
        maxNumberOfPersons: params.product.numberOfPersons,
        numberOfDays: params.product.days,
        minutes: params.product.durationMinutes,
        price: this.$options.filters.roundNumber(priceGross, 0),
        priceNet: this.$options.filters.roundNumber(priceNet, 0),
        currencyNet: params.product.season.supplier.currency,
        supplierName: params.product.season.supplier.name,
        seasonName: params.product.season.name,
        personCategoryName: params.price.personCategory.name + personCategoryAges,
        levelName: params.level.name,
        skiTypeName: params.product.skiType.name,
        lessonTypeName: params.product.lessonType.name,
        meetingPointName: null,
        seasonNameSupplier: params.product.season.name,
        personCategoryNameSupplier: params.price.personCategory.externalName + personCategoryAges,
        levelNameSupplier: params.level.externalName,
        skiTypeNameSupplier: params.product.skiType.identifier,
        lessonTypeNameSupplier: params.product.lessonType.identifier,
        meetingPointNameSupplier: null,
        areaId,
      };
      this.createSchool({
        bookingId: this.booking.id,
        bookingParticipantId: this.selectedBookingParticipantId,
        skiSchool,
      });
    },
    searchAvailableProducts() {
      if (!this.canSearch) {
        return;
      }
      const participant = this.participants.find(
        item => item.id === this.selectedBookingParticipantId,
      );
      const areaId = this.getSelectedAreaId();
      const bookingStay = this.getBookingStayByAreaId(areaId);
      this.loadAvailableSchoolPrices({
        areaId,
        participantAge: participant.age,
        arrivalDate: bookingStay.arrivalDate,
        departureDate: bookingStay.departureDate,
        targetCurrency: this.booking.currency,
      });
    },
    getBookingStayByAreaId(areaId) {
      return this.booking.stays.find(stay => stay.areaId === areaId);
    },
    getSelectedAreaId() {
      return this.selectedArea || this.defaultAreaId;
    },
    doRefresh() {
      this.loadSchoolsByBookingId(this.booking.id);
      this.searchAvailableProducts();
    },
    deleteProductDialog(skiSchoolId) {
      this.deleteSchool({ skiSchoolId, salesAgentUserId: this.user.id });
    },
    openConfirmDialog(type) {
      const vueComponent = this.$refs[type];
      if (vueComponent instanceof Array) {
        vueComponent[0].open();
      } else {
        vueComponent.open();
      }
    },
    onBookingParticipantChange(participantId) {
      this.setBookingParticipant(participantId);
      this.searchAvailableProducts();
    },
    onAreaChange(areaId) {
      this.selectedArea = areaId;
      this.searchAvailableProducts();
    },
  },
};
</script>

<template>
  <v-container v-if="booking && booking.id" class="pa-0 pt-3" grid-list-xl>
    <v-card class="pa-3 mx-auto" outlined>
      <v-layout class="py-3 px-3">
        <v-flex class="ma-0 pa-0" sm9 xs12>
          <v-select
            @change="onBookingParticipantChange($event)"
            :value="selectedBookingParticipantId"
            :items="areaParticipants"
            :loading="participantsLoading"
            :disabled="participantsLoading || !participants.length"
            :label="$t('ski.school.selects.booking-participant')"
            item-text="fullName"
            item-value="id"
          />
        </v-flex>
        <v-flex v-if="areas.length > 1" class="pl-2 ma-0 pa-0" sm2 xs12>
          <v-select
            @change="onAreaChange($event)"
            :value="defaultAreaId"
            :items="areas"
            :label="$t('ski.school.selects.area')"
            item-text="name"
            item-value="id"
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm1 xs12>
          <v-btn
            @click="doRefresh"
            :loading="loading"
            icon
          >
            <v-icon>cached</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
      <v-divider></v-divider>
      <v-layout class="px-3 pt-3">
        <h3 class="d-inline-block">
          {{ $t('ski.school.filters') }}
        <v-btn
          @click="setClearFilters"
          class="mb-2"
          color="orange"
          flat
          small
        >
          {{ $t('ski.school.clear') }}
        </v-btn>
        </h3>
      </v-layout>
      <v-layout class="pl-3 pr-3" wrap>
        <v-flex class="ma-0 pa-0" sm5 xs12>
          <v-select
            @change="setLessonType($event)"
            :value="selectedLessonType"
            :items="availableLessonTypes"
            :loading="loading"
            :disabled="!availableLessonTypes.length"
            :label="$t('ski.school.selects.lesson-type')"
            item-text="name"
            item-value="id"
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setWeekDayNumber($event)"
            :value="selectedWeekDayNumber"
            :items="availableWeekDays"
            :loading="loading"
            :disabled="!availableWeekDays.length"
            :label="$t('ski.school.selects.week-day')"
            item-text="name"
            item-value="id"
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setNumberOfDays($event)"
            :value="selectedNumberOfDays"
            :items="availableNumberOfDays"
            :loading="loading"
            :disabled="!availableNumberOfDays.length"
            :label="$t('ski.school.selects.number-of-days')"
          />
        </v-flex>
      </v-layout>
      <v-layout class="pl-3 pr-3" wrap>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setLessonDuration($event)"
            :value="selectedLessonDuration"
            :items="availableLessonDurations"
            :loading="loading"
            :disabled="!availableLessonDurations.length"
            :label="$t('ski.school.selects.lesson-duration')"
            item-text="name"
            item-value="id"
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm5 xs12>
          <v-autocomplete
            @change="setSkiTypes($event)"
            :value="selectedMultipleSkiTypes"
            :items="availableSkiTypes"
            :loading="loading"
            :disabled="!availableSkiTypes.length"
            :deletable-chips="true"
            :label="$t('ski.school.selects.multiple-ski-types')"
            item-text="name"
            item-value="id"
            clearable
            chips
            multiple
          />
        </v-flex>
        <v-flex class="pl-2 ma-0 pa-0" sm3 xs12>
          <v-select
            @change="setNumberOfPersons($event)"
            :value="selectedNumberOfPersons"
            :items="availableNumberOfPersons"
            :loading="loading"
            :disabled="!availableNumberOfPersons.length"
            :label="$t('ski.school.selects.number-of-persons')"
          />
        </v-flex>
      </v-layout>
    </v-card>
    <div class="text-center pa-1">
      <v-progress-linear v-show="loading" :indeterminate="true"/>
    </div>
    <v-alert
      :value="this.errorOccurred"
      color="error"
      icon="warning"
      outline
    >
      <span v-html="errorOccurred"></span>
    </v-alert>
    <div
      v-if="!Object.keys(mappedAvailableSchoolProducts).length && canSearch && !loading"
      class="py-3 font-weight-bold"
    >
      {{ $t('ski.school.no-available-school-products') }}
    </div>
    <v-container v-if="Object.keys(mappedAvailableSchoolProducts).length > 0" grid-list-md>
      <template v-for="(skiLessonItem, skiLessonKey) in mappedAvailableSchoolProducts">
        <div
          v-for="(personDayDurationItem, personDayDurationKey) in skiLessonItem"
          :key="`${skiLessonKey}-${personDayDurationKey}`"
          class="py-3 font-weight-bold"
        >
          <v-layout class="pb-0 mb-0 no-wrap" row>
            <v-flex sm4 xs12>
              <h2>
                {{ personDayDurationItem.lessonType.name }}
                <span class="font-weight-regular">({{ personDayDurationItem.skiType.name }})</span>
              </h2>
            </v-flex>
            <v-flex class="d-flex pt-3" sm8 xs12>
              <v-flex
                v-for="personCategory in availableSchoolProductsPersonCategoryHeaders"
                :key="personCategory.id"
                class="ma-0 pa-0 text-uppercase"
                sm4
                xs12
              >
                {{ personCategory.name }} ({{ $t('ski.school.years-from-to', {
                  from: personCategory.ageFrom,
                  to: personCategory.ageTo
                }) }})
              </v-flex>
            </v-flex>
          </v-layout>
          <v-layout
            v-for="(level, levelId) in personDayDurationItem.levels"
            :key="levelId"
            class="pb-0"
            row
          >
            <v-flex class="pt-3 pr-3" sm4 xs12>
              <div class="pl-4">
                <v-icon title="level">hdr_weak</v-icon> {{ level.name }}<br>
                <v-icon title="persons">people</v-icon>
                {{ personDayDurationItem.product.numberOfPersons }} {{ $t('ski.school.persons') }}
                <br>
                <v-icon title="days">calendar_today</v-icon>
                {{ personDayDurationItem.product.days }} {{ $t('ski.school.days') }}
                ({{ $t('ski.school.start-day') }}: {{ personDayDurationItem.weekDayName }})<br>
                <v-icon title="minutes">schedule</v-icon>
                {{ personDayDurationItem.product.durationMinutes }}
                {{ $t('ski.school.minutes-per-lesson') }}
              </div>
            </v-flex>
            <v-flex class="ma-0 pa-0 d-flex" wrap align-center sm8 xs12>
              <v-flex
                v-for="(price, priceKey) in level.prices"
                :key="`${priceKey}-${price.id}`"
                class="ma-0 pa-0"
                sm4
                xs12
              >
                <v-btn
                  v-if="price.id"
                  @click="createSkiSchoolProduct({ product: personDayDurationItem.product, level, price })"
                  :disabled="loading"
                  :loading="loading"
                  :title="$t('ski.school.add-product')"
                  outline
                >
                  {{ price.priceGross }} {{ booking.currency }}
                </v-btn>
                <template v-if="price.bookingSchoolProductId">
                  (Already added)
                </template>
              </v-flex>
            </v-flex>
          </v-layout>
        </div>
      </template>
      <v-divider></v-divider>
    </v-container>
  </v-container>
</template>



// WEBPACK FOOTER //
// src/pages/booking/ski/school/SkiSchoolAvailableProducts.vue